import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import logo from '../assets/navbar/1.png';
import backgroundImage from '../assets/navbar/pngtree-closeup-top-view-of-cinnamon-powder-texture-on-ground-cinnamon-bark-image_13721250.png';
import { FaWhatsapp, FaFacebook, FaInstagram, FaTiktok, FaMapMarkerAlt, FaShoppingCart, FaBars } from 'react-icons/fa';

const CINNAMON_COLOR = '#C19A6B';

function Navbar() {
    const cinnamonColor = CINNAMON_COLOR;
    const [cartCount, setCartCount] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [email, setEmail] = useState(() => localStorage.getItem('customerEmail') || '');

    useEffect(() => {
        let isCurrent = true;

        const fetchCartCount = async () => {
            if (!email) {
                setCartCount(0);
                return;
            }
            try {
                const response = await fetch(`https://galleceylonspices.com/api/cart?email=${encodeURIComponent(email)}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch cart count');
                }
                const data = await response.json();
                if (isCurrent) {
                    setCartCount(data.length);
                }
            } catch (error) {
                console.error('Error fetching cart count:', error);
                if (isCurrent) {
                    setCartCount(0);
                }
            }
        };

        fetchCartCount();
        return () => {
            isCurrent = false;
        };
    }, [email]);

    useEffect(() => {
        const handleStorageChange = () => {
            setEmail(localStorage.getItem('customerEmail') || '');
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen((isOpen) => !isOpen);
    };

    return (
        <div className="bg-white text-dark shadow-sm position-relative">
            {/* Top Section with Background Image */}
            <div
                className="container-fluid py-3 border-bottom border-secondary bg-white navbar-top-section"
                style={{
                    backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), url(${backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    minHeight: '100px',
                    position: 'relative'
                }}
            >
                <div className="row align-items-center h-100">
                    {/* Logo - Centered on All Devices */}
                    <div className="col-12 col-md-6 d-flex justify-content-center align-items-center position-relative navbar-logo-wrapper">
                        <img
                            src={logo}
                            alt="Galle Ceylon Spices Logo"
                            className="img-fluid navbar-logo"
                            style={{
                                maxHeight: 'clamp(80px, 15vw, 120px)',
                                transition: 'transform 0.3s ease-in-out'
                            }}
                        />
                        {/* Menu Button for Mobile */}
                        <button
                            className="navbar-toggler d-md-none"
                            onClick={toggleMenu}
                            aria-label="Toggle navigation menu"
                            style={{
                                border: 'none',
                                background: 'transparent',
                                fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                                cursor: 'pointer',
                                position: 'absolute',
                                top: '50%',
                                right: '10px',
                                transform: 'translateY(-50%)'
                            }}
                        >
                            <FaBars style={{ color: cinnamonColor }} />
                        </button>
                    </div>
                    {/* Contact/Social - Hidden on Mobile Portrait */}
                    <div className="col-12 col-md-6 d-flex justify-content-end align-items-center navbar-contact-wrapper">
                        <div className="d-none d-md-block">
                            <div className="mb-2">
                                <a
                                    href="tel:+94777465790"
                                    className="text-dark fw-medium text-decoration-none"
                                    style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', fontFamily: 'Copperplate' }}
                                    aria-label="Call us at 077-7465790"
                                >
                                    Call Us: 077-7465790
                                </a>
                            </div>
                            <div className="d-flex gap-3">
                                <a
                                    href="https://wa.me/+94779291829"
                                    className="text-dark hover-scale"
                                    aria-label="Contact us on WhatsApp"
                                >
                                    <FaWhatsapp size={20} />
                                </a>
                                <a
                                    href="https://facebook.com/galleceylonspices"
                                    className="text-dark hover-scale"
                                    aria-label="Visit our Facebook page"
                                >
                                    <FaFacebook size={20} />
                                </a>
                                <a
                                    href="https://instagram.com/galleceylonspices"
                                    className="text-dark hover-scale"
                                    aria-label="Visit our Instagram page"
                                >
                                    <FaInstagram size={20} />
                                </a>
                                <a
                                    href="https://tiktok.com/@galleceylonspices"
                                    className="text-dark hover-scale"
                                    aria-label="Visit our TikTok page"
                                >
                                    <FaTiktok size={20} />
                                </a>
                                <a
                                    href="https://maps.google.com/?q=448/2,+Nawadagala,+Elpitiya,+Sri+Lanka"
                                    className="text-dark hover-scale"
                                    aria-label="View our location on Google Maps"
                                >
                                    <FaMapMarkerAlt size={20} />
                                </a>
                                <a href="/cart" className="text-dark hover-scale position-relative" aria-label="View shopping cart">
                                    <FaShoppingCart size={20} />
                                    {cartCount > 0 && (
                                        <span
                                            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                                            style={{ fontSize: 'clamp(0.6rem, 1.5vw, 0.7rem)' }}
                                        >
                                            {cartCount}
                                        </span>
                                    )}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Section - Navigation Links (Hidden on Mobile) */}
            <nav className="navbar navbar-expand-lg bg-white d-none d-md-block">
                <div className="container">
                    <div className="collapse navbar-collapse" id="navbarNav">
                        <ul className="navbar-nav mx-auto gap-3">
                            <li className="nav-item">
                                <NavLink
                                    className="nav-link fw-semibold px-3 py-2 rounded"
                                    to="/"
                                    style={({ isActive }) => ({
                                        color: isActive ? '#000000' : cinnamonColor,
                                        textDecoration: isActive ? 'underline' : 'none',
                                        backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                        fontFamily: 'Copperplate',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        transition: 'all 0.3s ease-in-out'
                                    })}
                                >
                                    HOME
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="nav-link fw-semibold px-3 py-2 rounded"
                                    to="/about"
                                    style={({ isActive }) => ({
                                        color: isActive ? '#000000' : cinnamonColor,
                                        textDecoration: isActive ? 'underline' : 'none',
                                        backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                        fontFamily: 'Copperplate',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        transition: 'all 0.3s ease-in-out'
                                    })}
                                >
                                    ABOUT US
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="nav-link fw-semibold px-3 py-2 rounded"
                                    to="/history"
                                    style={({ isActive }) => ({
                                        color: isActive ? '#000000' : cinnamonColor,
                                        textDecoration: isActive ? 'underline' : 'none',
                                        backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                        fontFamily: 'Copperplate',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        transition: 'all 0.3s ease-in-out'
                                    })}
                                >
                                    HISTORY OF CINNAMON
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="nav-link fw-semibold px-3 py-2 rounded"
                                    to="/technology"
                                    style={({ isActive }) => ({
                                        color: isActive ? '#000000' : cinnamonColor,
                                        textDecoration: isActive ? 'underline' : 'none',
                                        backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                        fontFamily: 'Copperplate',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        transition: 'all 0.3s ease-in-out'
                                    })}
                                >
                                    TECHNOLOGY
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="nav-link fw-semibold px-3 py-2 rounded"
                                    to="/products"
                                    style={({ isActive }) => ({
                                        color: isActive ? '#000000' : cinnamonColor,
                                        textDecoration: isActive ? 'underline' : 'none',
                                        backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                        fontFamily: 'Copperplate',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        transition: 'all 0.3s ease-in-out'
                                    })}
                                >
                                    OUR PRODUCTS
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="nav-link fw-semibold px-3 py-2 rounded"
                                    to="/shop"
                                    style={({ isActive }) => ({
                                        color: isActive ? '#000000' : cinnamonColor,
                                        textDecoration: isActive ? 'underline' : 'none',
                                        backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                        fontFamily: 'Copperplate',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        transition: 'all 0.3s ease-in-out'
                                    })}
                                >
                                    SHOP NOW
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="nav-link fw-semibold px-3 py-2 rounded"
                                    to="/contact"
                                    style={({ isActive }) => ({
                                        color: isActive ? '#000000' : cinnamonColor,
                                        textDecoration: isActive ? 'underline' : 'none',
                                        backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                        fontFamily: 'Copperplate',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        transition: 'all 0.3s ease-in-out'
                                    })}
                                >
                                    CONTACT US
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="nav-link fw-semibold px-3 py-2 rounded"
                                    to="/cart"
                                    style={({ isActive }) => ({
                                        color: isActive ? '#000000' : cinnamonColor,
                                        textDecoration: isActive ? 'underline' : 'none',
                                        backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                        fontFamily: 'Copperplate',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        transition: 'all 0.3s ease-in-out'
                                    })}
                                >
                                    <FaShoppingCart className="me-1" />
                                </NavLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            {/* Sidebar Menu for Mobile */}
            <div
                className={`sidebar ${isMenuOpen ? 'open' : ''}`}
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: 'clamp(280px, 70vw, 320px)',
                    height: '100%',
                    backgroundColor: '#fff',
                    boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.2)',
                    zIndex: 1000,
                    transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s ease-in-out',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <button
                    onClick={toggleMenu}
                    aria-label="Close navigation menu"
                    style={{
                        border: 'none',
                        background: 'transparent',
                        fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                        cursor: 'pointer',
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        color: cinnamonColor
                    }}
                >
                    ×
                </button>
                <ul className="list-unstyled text-center" style={{ marginTop: '60px' }}>
                    <li className="mb-3">
                        <a
                            href="tel:+94779291829"
                            className="text-dark fw-medium text-decoration-none"
                            style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', fontFamily: 'Copperplate' }}
                            aria-label="Call us at 077-9291829"
                        >
                            Call Us: 077-9291829
                        </a>
                    </li>
                    <li className="mb-3 d-flex justify-content-center gap-3">
                        <a
                            href="https://wa.me/+94779291829"
                            className="text-dark hover-scale"
                            aria-label="Contact us on WhatsApp"
                        >
                            <FaWhatsapp size={20} />
                        </a>
                        <a
                            href="https://facebook.com/galleceylonspices"
                            className="text-dark hover-scale"
                            aria-label="Visit our Facebook page"
                        >
                            <FaFacebook size={20} />
                        </a>
                        <a
                            href="https://instagram.com/galleceylonspices"
                            className="text-dark hover-scale"
                            aria-label="Visit our Instagram page"
                        >
                            <FaInstagram size={20} />
                        </a>
                        <a
                            href="https://tiktok.com/@galleceylonspices"
                            className="text-dark hover-scale"
                            aria-label="Visit our TikTok page"
                        >
                            <FaTiktok size={20} />
                        </a>
                        <a
                            href="https://maps.google.com/?q=448/2,+Nawadagala,+Elpitiya,+Sri+Lanka"
                            className="text-dark hover-scale"
                            aria-label="View our location on Google Maps"
                        >
                            <FaMapMarkerAlt size={20} />
                        </a>
                        <a href="/cart" className="text-dark hover-scale position-relative" aria-label="View shopping cart">
                            <FaShoppingCart size={20} />
                            {cartCount > 0 && (
                                <span
                                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                                    style={{ fontSize: 'clamp(0.6rem, 1.5vw, 0.7rem)' }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </a>
                    </li>
                    <li className="mb-3">
                        <NavLink
                            to="/"
                            className="nav-link fw-semibold px-3 py-2 rounded"
                            style={({ isActive }) => ({
                                color: isActive ? '#000000' : cinnamonColor,
                                textDecoration: isActive ? 'underline' : 'none',
                                backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                fontFamily: 'Copperplate',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                display: 'block',
                                transition: 'all 0.3s ease-in-out'
                            })}
                            onClick={toggleMenu}
                        >
                            HOME
                        </NavLink>
                    </li>
                    <li className="mb-3">
                        <NavLink
                            to="/about"
                            className="nav-link fw-semibold px-3 py-2 rounded"
                            style={({ isActive }) => ({
                                color: isActive ? '#000000' : cinnamonColor,
                                textDecoration: isActive ? 'underline' : 'none',
                                backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                fontFamily: 'Copperplate',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                display: 'block',
                                transition: 'all 0.3s ease-in-out'
                            })}
                            onClick={toggleMenu}
                        >
                            ABOUT US
                        </NavLink>
                    </li>
                    <li className="mb-3">
                        <NavLink
                            to="/history"
                            className="nav-link fw-semibold px-3 py-2 rounded"
                            style={({ isActive }) => ({
                                color: isActive ? '#000000' : cinnamonColor,
                                textDecoration: isActive ? 'underline' : 'none',
                                backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                fontFamily: 'Copperplate',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                display: 'block',
                                transition: 'all 0.3s ease-in-out'
                            })}
                            onClick={toggleMenu}
                        >
                            HISTORY OF CINNAMON
                        </NavLink>
                    </li>
                    <li className="mb-3">
                        <NavLink
                            to="/technology"
                            className="nav-link fw-semibold px-3 py-2 rounded"
                            style={({ isActive }) => ({
                                color: isActive ? '#000000' : cinnamonColor,
                                textDecoration: isActive ? 'underline' : 'none',
                                backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                fontFamily: 'Copperplate',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                display: 'block',
                                transition: 'all 0.3s ease-in-out'
                            })}
                            onClick={toggleMenu}
                        >
                            TECHNOLOGY
                        </NavLink>
                    </li>
                    <li className="mb-3">
                        <NavLink
                            to="/products"
                            className="nav-link fw-semibold px-3 py-2 rounded"
                            style={({ isActive }) => ({
                                color: isActive ? '#000000' : cinnamonColor,
                                textDecoration: isActive ? 'underline' : 'none',
                                backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                fontFamily: 'Copperplate',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                display: 'block',
                                transition: 'all 0.3s ease-in-out'
                            })}
                            onClick={toggleMenu}
                        >
                            OUR PRODUCTS
                        </NavLink>
                    </li>
                    <li className="mb-3">
                        <NavLink
                            to="/shop"
                            className="nav-link fw-semibold px-3 py-2 rounded"
                            style={({ isActive }) => ({
                                color: isActive ? '#000000' : cinnamonColor,
                                textDecoration: isActive ? 'underline' : 'none',
                                backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                fontFamily: 'Copperplate',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                display: 'block',
                                transition: 'all 0.3s ease-in-out'
                            })}
                            onClick={toggleMenu}
                        >
                            SHOP NOW
                        </NavLink>
                    </li>
                    <li className="mb-3">
                        <NavLink
                            to="/contact"
                            className="nav-link fw-semibold px-3 py-2 rounded"
                            style={({ isActive }) => ({
                                color: isActive ? '#000000' : cinnamonColor,
                                textDecoration: isActive ? 'underline' : 'none',
                                backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                fontFamily: 'Copperplate',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                display: 'block',
                                transition: 'all 0.3s ease-in-out'
                            })}
                            onClick={toggleMenu}
                        >
                            CONTACT US
                        </NavLink>
                    </li>
                    <li className="mb-3">
                        <NavLink
                            to="/cart"
                            className="nav-link fw-semibold px-3 py-2 rounded"
                            style={({ isActive }) => ({
                                color: isActive ? '#000000' : cinnamonColor,
                                textDecoration: isActive ? 'underline' : 'none',
                                backgroundColor: isActive ? '#F5F0E8' : 'transparent',
                                fontFamily: 'Copperplate',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                display: 'block',
                                transition: 'all 0.3s ease-in-out'
                            })}
                            onClick={toggleMenu}
                        >
                            <FaShoppingCart className="me-1" /> CART
                        </NavLink>
                    </li>
                </ul>
            </div>

            {/* Inline CSS for professional styling and responsiveness */}
            <style>
                {`
                    .navbar-top-section {
                        padding-left: clamp(1rem, 3vw, 2rem) !important;
                        padding-right: clamp(1rem, 3vw, 2rem) !important;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    }
                    .hover-scale {
                        transition: transform 0.3s ease-in-out, color 0.3s ease-in-out;
                    }
                    .hover-scale:hover, .hover-scale:focus {
                        transform: scale(1.15);
                        color: ${cinnamonColor} !important;
                    }
                    .nav-link {
                        transition: color 0.3s ease-in-out, background-color 0.3s ease-in-out;
                    }
                    .nav-link:hover, .nav-link:focus {
                        color: ${cinnamonColor} !important;
                        background-color: #F5F0E8;
                    }
                    .sidebar {
                        transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                    }
                    .sidebar.open {
                        transform: translateX(0);
                    }
                    .sidebar ul li a {
                        transition: all 0.3s ease-in-out;
                    }
                    .sidebar ul li a:hover, .sidebar ul li a:focus {
                        background-color: #F5F0E8;
                        color: ${cinnamonColor} !important;
                    }
                    .navbar-nav .nav-link {
                        border-radius: 8px;
                        padding: 8px 16px !important;
                    }
                    @media (min-width: 769px) {
                        .navbar-logo-wrapper {
                            position: absolute !important;
                            top: 50%;
                            left: 50%;
                            width: auto;
                            padding: 0;
                            transform: translate(-50%, -50%);
                            z-index: 1;
                        }
                        .navbar-contact-wrapper {
                            position: absolute !important;
                            top: 50%;
                            right: clamp(1rem, 3vw, 2rem);
                            width: auto;
                            padding: 0;
                            transform: translateY(-50%);
                            z-index: 1;
                        }
                    }
                    @media (max-width: 576px) {
                        .navbar-top-section {
                            padding-left: 0.5rem !important;
                            padding-right: 0.5rem !important;
                            min-height: 80px !important;
                        }
                        .py-3 {
                            padding-top: 0.5rem !important;
                            padding-bottom: 0.5rem !important;
                        }
                        .d-md-block {
                            display: none !important;
                        }
                        .navbar {
                            display: none !important;
                        }
                        .sidebar {
                            width: clamp(260px, 80vw, 300px) !important;
                            padding: 15px !important;
                        }
                        .sidebar ul {
                            margin-top: 40px !important;
                        }
                        .sidebar .mb-3 {
                            margin-bottom: 1rem !important;
                        }
                        .navbar-logo {
                            max-height: 80px !important;
                        }
                    }
                    @media (max-width: 576px) and (orientation: landscape) {
                        .navbar-top-section {
                            padding-left: 0.5rem !important;
                            padding-right: 0.5rem !important;
                            min-height: 60px !important;
                        }
                        .py-3 {
                            padding-top: 0.25rem !important;
                            padding-bottom: 0.25rem !important;
                        }
                        .sidebar {
                            width: clamp(220px, 60vw, 260px) !important;
                            padding: 10px !important;
                        }
                        .navbar-logo {
                            max-height: 60px !important;
                        }
                    }
                    @media (min-width: 577px) and (max-width: 768px) {
                        .navbar-top-section {
                            padding-left: 1rem !important;
                            padding-right: 1rem !important;
                            min-height: 90px !important;
                        }
                        .py-3 {
                            padding-top: 1rem !important;
                            padding-bottom: 1rem !important;
                        }
                        .navbar-logo {
                            max-height: 100px !important;
                        }
                    }
                    @media (min-width: 769px) and (max-width: 1024px) {
                        .navbar-top-section {
                            padding-left: 1.5rem !important;
                            padding-right: 1.5rem !important;
                            min-height: 100px !important;
                        }
                        .py-3 {
                            padding-top: 1.5rem !important;
                            padding-bottom: 1.5rem !important;
                        }
                        .sidebar {
                            display: none !important;
                        }
                        .navbar-logo {
                            max-height: 110px !important;
                        }
                    }
                    @media (min-width: 1025px) {
                        .navbar-top-section {
                            padding-left: 2rem !important;
                            padding-right: 2rem !important;
                            min-height: 120px !important;
                        }
                        .py-3 {
                            padding-top: 2rem !important;
                            padding-bottom: 2rem !important;
                        }
                        .sidebar {
                            display: none !important;
                        }
                        .navbar-logo {
                            max-height: 120px !important;
                        }
                    }
                `}
            </style>
        </div>
    );
}

export default Navbar;